import React, { PropsWithChildren, useContext } from 'react';
import { Configuration } from '../data/configuration.js';
import { Utilities } from '../utilities/utilities.js';
import { AppContext, ViewStates } from './app.js';
import RegionSelector from './ui/region-selector.tsx';
import RestartOptions from './ui/restart-options.tsx';
import SettingsOptions from './ui/settings-options.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface VehicleViewProps {
	shouldHideNextIcon?: boolean;
	shouldHideSummary?: boolean;
	onBackButtonClicked?: () => void;
	onNextButtonClicked?: () => void;
}

interface VehicleContentProps {
	shouldHideSummary?: boolean;
	handleClose?: () => void;
}

interface VehicleSummaryProps {
	shouldHideSummary?: boolean;
	onBackButtonClicked?: () => void;
	onNextButtonClicked?: () => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function VehicleView(props: PropsWithChildren<VehicleViewProps>) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// return the HTML.
	return (
		<div
			id="vehicle-view"
			className={context.isDrawerOpen ? 'drawer-open' : ''}
		>
			<VehicleHeader />
			<VehicleContent
				shouldHideSummary={props.shouldHideSummary}
				handleClose={() => context.setIsDrawerOpen(false)}
			>
				{props.children}
			</VehicleContent>
			<VehicleSummary
				shouldHideSummary={props.shouldHideSummary}
				onBackButtonClicked={props.onBackButtonClicked}
				onNextButtonClicked={props.onNextButtonClicked}
			/>
		</div>
	);
}

export function VehicleMenu() {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// if a menu item is clicked...
	const handleMenuItemClicked = (viewState: ViewStates) => {

		// change to show the given view, unless it is already showing + close menu.
		// n.b. React will do nothing in this instance, but it feels safer to check.
		if (context.viewState !== viewState) {
			context.handleChangeView(viewState);
		}

		context.setIsDrawerOpen(true);
	}

	// return the HTML depending on state, React.Fragment to make up for empty tags.
	return (
		<React.Fragment>
			{context.isDrawerOpen ? (
				<div id="canvas-overlay-header">
					<a role="button" className="vehicle-menu-toggle" onClick={() => context.setIsDrawerOpen(false)}>
						<img src="assets/images/icons/hamburger.svg" width="35" height="35" alt="Menu" />
					</a>
				</div>
			) : (
				<div id="vehicle-menu">
					<div id="vehicle-menu-content">
						<div id="vehicle-menu-controls">
							<RegionSelector />
							<a role="button" id="vehicle-menu-close-button" className="vehicle-menu-toggle" onClick={() => context.setIsDrawerOpen(true)}>
								<img src="assets/images/icons/close.svg" width="35" height="35" alt="Close" />
							</a>
						</div>
						<div id="vehicle-menu-body">
							<ul>
								{!Configuration.isUsRegion() && (
									<li>
										<a role="button"
											className={`vehicle-menu-menu-item${context.viewState === ViewStates.Gearbox ? ' selected' : ''}`}
											onClick={() => handleMenuItemClicked(ViewStates.Gearbox)}>
											 Gearbox
										</a>
									</li>
								)}
								<li>
									<a role="button"
										className={`vehicle-menu-menu-item${context.viewState === ViewStates.Paintwork ? ' selected' : ''}`}
										onClick={() => handleMenuItemClicked(ViewStates.Paintwork)}>
										Paintwork
									</a>
								</li>
								<li>
									<a role="button"
										className={`vehicle-menu-menu-item${context.viewState === ViewStates.Wheels ? ' selected' : ''}`}
										onClick={() => handleMenuItemClicked(ViewStates.Wheels)}>
										Wheels
									</a>
								</li>
								<li>
									<a role="button"
										className={`vehicle-menu-menu-item${context.viewState === ViewStates.Interior ? ' selected' : ''}`}
										onClick={() => handleMenuItemClicked(ViewStates.Interior)}>
										Interior
									</a>
								</li>
								<li>
									<a role="button"
										className={`vehicle-menu-menu-item${context.viewState === ViewStates.Exterior ? ' selected' : ''}`}
										onClick={() => handleMenuItemClicked(ViewStates.Exterior)}>
										Exterior
									</a>
								</li>
								<li>
									<a role="button"
										className={`vehicle-menu-menu-item${context.viewState === ViewStates.Accessories ? ' selected' : ''}`}
										onClick={() => handleMenuItemClicked(ViewStates.Accessories)}>
										Accessories
									</a>
								</li>
								<li>
									<a role="button"
										className={`vehicle-menu-menu-item${context.viewState === ViewStates.Summary ? ' selected' : ''}`}
										onClick={() => handleMenuItemClicked(ViewStates.Summary)}>
										Summary
									</a>
								</li>
							</ul>
							<button
								role="button"
								id="vehicle-menu-more-info-button"
								onClick={() => handleMenuItemClicked(ViewStates.ImportantInformation)}
							>
								MORE INFORMATION
							</button>
							<div id="vehicle-menu-configuration-button-container">
								<button
									type="button"
									id="vehicle-menu-save-configuration-button"
									onClick={() => handleMenuItemClicked(ViewStates.SendToDealer)}
								>
									SAVE CONFIGURATION
								</button>
								<button
									type="button"
									id="vehicle-menu-load-configuration-button"
									onClick={() => handleMenuItemClicked(ViewStates.LoadConfiguration)}
								>
									LOAD CONFIGURATION
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</React.Fragment>
	);
}

export function VehicleHeader() {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// return the HTML.
	return (
		<div id="vehicle-header" ref={(element: HTMLDivElement) => setReference(element)}>
			<div id="canvas-overlay">
				<div id="canvas-overlay-top">
					<SettingsOptions
						trigger={(onClick: () => void) => (
							<div id="vehicle-summary-settings">
								<a role="button" onClick={onClick}>
									<img src="assets/images/icons/settings.svg" width="34" height="34" alt="Settings" />
								</a>
							</div>
						)}
					/>
				</div>
				<div id="canvas-overlay-footer">
					<div className="canvas-footer-item">
					</div>
					<div className="canvas-footer-item" >
						<div id="orbital-icon">
							<img src={'assets/images/icons/360-arrow.svg'} width={60} height={19} />
						</div>
					</div>

					{/* the right hand group: restart leads, so it sits to the left of the orientation hint on mobile. */}
					<div className="canvas-footer-item canvas-footer-item-end">
						<RestartOptions />
						<div id="canvas-overlay-expand-contract-icon"></div>
					</div>

				</div>
			</div>
		</div>
	);
}

export function VehicleContent(props: PropsWithChildren<VehicleContentProps>) {
	return (
		<div id="vehicle-content" ref={(element: HTMLDivElement) => setReference(element, props.shouldHideSummary)}>
			<div className="close desktop-drawer-close-icon" onClick={props.handleClose}>
				<img src="assets/images/icons/up-arrow.svg" width="auto" height="12" alt="Close" />
			</div>
			{props.children}
		</div>
	);
}

export function VehicleSummary(props: VehicleSummaryProps) {
	
	// returns the total cost or a suitable message (if an invalid option has been selected).
	const totalCost = () => {
		if (!Configuration.invalidOptionSelected) {
			return Utilities.asCurrency(Configuration.totalCost, Configuration.regionCode);	
		}
		return 'Contact Dealer';
	};

	// disables the next button if no handler has been provided for it (greying it out and preventing clicks).
	const setNextButtonRef = (element: HTMLAnchorElement) => {
		if (element) {
			if (!props.onNextButtonClicked) {
				element.classList.add('disabled');
			}
			else {
				element.classList.remove('disabled');
			}
		}
	};

	// return the HTML.
	return (
		<div id="vehicle-summary" ref={(element: HTMLDivElement) => setReference(element, props.shouldHideSummary)}>
			<div id="vehicle-summary-total">
				{totalCost()}
			</div>
			<div id="vehicle-summary-controls">
				<div id="vehicle-summary-navigation">
					<a role="button" onClick={props.onBackButtonClicked} id="vehicle-summary-prev">
						<img src="assets/images/icons/left-arrow.svg" width="34" height="34" alt="Previous" />
					</a>
					<a role="button" onClick={props.onNextButtonClicked} ref={setNextButtonRef}>
						<img src="assets/images/icons/right-arrow.svg" width="34" height="34" alt="Next" />
					</a>
				</div>
			</div>
		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// Adds or removes CSS classes to/from the Header, Summary and Content elements, according to the given optional parameters.
function setReference(element: HTMLDivElement, shouldHideSummary?: boolean) {
	if (element) {

		// optionally hides the summary and alters spacing on the contents.
		if (shouldHideSummary !== undefined) {
			if (shouldHideSummary) {
				element.classList.add('hide-summary');
			}
			else {
				element.classList.remove('hide-summary');
			}
		}
	}
}

